import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    setWaMapping, markWaEdited as markWaEditedAction, setWaSaveState,
} from '../../../redux/reducers/smartSendWhatsAppSlice';
import { buildWaSaveBody } from './waTokens';
import { selectWaLegState, selectWaLocked1b, WaRootState } from './selectors';
import { isConfirmResetArmed, clearConfirmReset } from '../confirmReset';

// SmartSend part B — the WhatsApp autosave. 750 ms trailing, single-flight with a trailing
// coalesce, monotonic `editSeq`. The pattern is COPIED from `SmartSendScreen.tsx:317-363`
// deliberately: two debounced savers on one screen that behave differently is how an edit gets
// stranded under a bar that says "saved".
//
// 🔴 THE WRITE LOCK IS DEFINED BY EFFECT, NOT BY A LIST OF CONTROLS (invariants 24 and 26).
// `canWrite` below is the ONE choke point: when it is false, `markWaEdited` is a no-op and
// `saveWaMapping` returns without dispatching. Disabling buttons is not enough — the contract
// records three separate rounds (AB-01, Q1-2) where a component-by-component list missed a
// write path that existed in the live tree (the suggestion chip, `openSummary`, the test
// dialog). If a user action can still produce a write while a money flag is lit, the invariant
// is broken regardless of how the buttons look.

const DEBOUNCE_MS = 750;

export interface UseWaAutosave {
    /** The single place a WhatsApp edit is recorded. No-op while the tab is locked. */
    markWaEdited: () => void;
    /** Persist now (the explicit "אשר תבנית" click, and the pre-summary save). */
    // 12-fix-wave 7.4(d) — `confirmReset` resends the save with `ConfirmReset: true` (the 409 dialog).
    saveWaMapping: (o?: { confirmReset?: boolean }) => Promise<{ ok: boolean; message: string | null }>;
    /** Cancel a pending debounce — call before any action that itself saves. */
    cancelPending: () => void;
    /** False ⇒ every write path in the tab must be inert. */
    canWrite: boolean;
}

// 09-channel-units — `campaignId` is the WhatsApp UNIT's own id (a WACampaignID) and `channel` is 3:
// the screen passes the unit it carries, never the e-mail campaign. 0 ⇒ the page has no WhatsApp
// unit and nothing is ever posted.
// 12-fix-wave 7.4(d) — `opts.pageChannel` + `opts.pageId` are the mapping page's container (the key
// of the reset confirmation in sessionStorage, `<channel>-<pathId>`); `opts.paused` stops the debounce
// and the trailing re-fire (an explicit `saveWaMapping` call still runs — the reset dialog's resend is one).
export const useWaAutosave = (
    campaignId: number, channel = 1,
    opts: { pageChannel?: number | null; pageId?: number | null; paused?: boolean } = {},
): UseWaAutosave => {
    const dispatch = useDispatch();
    const pageChannel = opts.pageChannel ?? null;
    const pageId = opts.pageId ?? null;
    const paused = !!opts.paused;
    const pausedRef = useRef(paused);
    pausedRef.current = paused;
    // A trailing re-fire that outlived the page never goes out (it could carry, and use up, the reset
    // confirmation the exit just armed).
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
    const wa = useSelector((s: any) => s.smartSendWa);
    // The source ON SCREEN — the same two values the e-mail save posts (SmartSendScreen
    // `buildSaveRequest`). The server re-binds the unit's own row when they differ from it.
    const screenSourceId: number | null = useSelector((s: any) => s.smartSend?.dataSource?.DataSourceID ?? null);
    const screenVersionId: number | null = useSelector((s: any) => s.smartSend?.lockedVersionId ?? null);
    const legState = useSelector((s: WaRootState) => selectWaLegState(s));
    const locked1b = useSelector((s: WaRootState) => selectWaLocked1b(s));

    const timer = useRef<any>(null);
    const inFlight = useRef(false);
    const pending = useRef(false);
    const editSeq = useRef(0);

    // ── who may write ───────────────────────────────────────────────────────────────────────
    // (2) locked      — the WhatsApp campaign has sent; `_SetMapping` answers -2 anyway.
    // (5) headerDeleted — read-only in full; the leg is now the evidence key for the report,
    //                     the supervisor mail and "view message".
    // (1b) chargedNoHeader — the money lock: the first edit would be the first POST, would run
    //                     EnsureHeader, would overwrite `leg.WhatsAppCampaignID`, and would
    //                     erase `LastErrorCharged` FOREVER (invariant 26).
    // (3) waiting with EmailCampaignStatus = 2 — `Campaigns.Status = 2 ⇒ -6` fires before any
    //     other gate, so every autosave would come back 409 WA_EDIT_BLOCKED_DURING_SEND. The
    //     screen disables editing up front instead of teaching the operator to ignore an error
    //     (v1.9 R6 L1-2/L6-8). At EmailCampaignStatus = 4 the map IS editable — the contract
    //     says so explicitly, and narrowing it would lock state (3)/(4), which is itself a
    //     breach of invariant 26.
    const emailSending = wa?.waMapping?.EmailCampaignStatus === 2;
    const canWrite = !(legState === 'locked' || legState === 'headerDeleted' || locked1b || emailSending);

    const cancelPending = useCallback(() => {
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
        pending.current = false;
    }, []);

    const markWaEdited = useCallback(() => {
        if (!canWrite) return;
        editSeq.current += 1;
        dispatch(markWaEditedAction());
    }, [canWrite, dispatch]);

    const persist = useCallback(async (o?: { confirmReset?: boolean }): Promise<{ ok: boolean; message: string | null }> => {
        if (!canWrite) return { ok: true, message: null };
        // No unit on this page ⇒ there is nothing a WhatsApp save could be addressed to.
        if (!campaignId || campaignId <= 0) return { ok: true, message: null };
        const templateId: string | null = wa?.whatsAppTemplateId ?? null;
        // Without a template there is nothing the server could store: `_SetMapping` answers -13
        // on a NULL template id. The picker is what chooses one; this is not an error state.
        if (!templateId) return { ok: false, message: 'WA_TEMPLATE_NOT_APPROVED' };

        if (inFlight.current) { pending.current = true; return { ok: true, message: null }; }
        inFlight.current = true;
        dispatch(setWaSaveState({ state: 'saving' }));

        // Snapshot on the SAME line of execution as the request body, so `seq` describes exactly
        // the state this request carries — the editSeq rule of SmartSendScreen, for the same
        // reason: a 200 that lands after a later edit must not clear `waDirty`.
        const seq = editSeq.current;
        const body = buildWaSaveBody({
            templateId,
            fromNumber: wa?.whatsAppFromNumber ?? null,
            placeholderCount: wa?.waPlaceholderCount ?? 0,
            // The unit's GET answers MediaUrl NULL on channel 3 (_GetMapping_Stage :125), so this
            // is the chosen template's own. NEVER omitted — `NULL` means "text template" to the
            // SP, not "leave unchanged". (09-channel-units: a template with media cannot be a unit —
            // POST WhatsApp/Container answers 409 WA_MEDIA_NOT_SUPPORTED.)
            mediaUrl: wa?.waMapping?.MediaUrl
                ?? (wa?.waTemplates ?? []).find((t: any) => t.TemplateId === templateId)?.MediaUrl
                ?? null,
            // 🔴 v1.23 — sent only when the operator has actually moved the channel switch.
            // `null` ⇒ the field is omitted ⇒ `ISNULL(@prm_SuppressEmail, SuppressEmail)` keeps
            // whatever the leg holds. This is what makes every OTHER save on the screen — a
            // variable binding, a static value, "אשר תבנית" — incapable of switching the e-mail
            // back on behind the operator's back.
            suppressEmail: wa?.suppressEmailDirty ? !!wa?.suppressEmail : null,
            map: wa?.waVariableMap ?? {},
            dataSourceId: screenSourceId,
            dataSourceVersionId: screenVersionId,
            // 12-fix-wave 7.4(d) — while the page's reset confirmation is armed, or on the dialog's resend.
            confirmReset: !!o?.confirmReset || isConfirmResetArmed(pageChannel, pageId),
        });

        const res: any = await dispatch(setWaMapping({ campaignId, body, channel }) as any);
        inFlight.current = false;
        const p = res && res.payload ? res.payload : {};
        const good = p.StatusCode === 200 || p.StatusCode === 201;
        const superseded = editSeq.current !== seq;
        // 12-fix-wave 7.4(d) — the first save that CARRIED the confirmation and answered 200 ends it.
        if (good && body.ConfirmReset) clearConfirmReset(pageChannel, pageId);

        if (good && superseded) {
            // The screen genuinely holds newer work. Stay on 'saving' and re-fire below; the
            // reducer already cleared waDirty, so re-arm it through the same choke point.
            dispatch(setWaSaveState({ state: 'saving' }));
            dispatch(markWaEditedAction());
        }

        // 12-fix-wave 7.4(d) — no trailing re-fire after these two answers: the page holds autosave
        // for them (the reset dialog / a closed send), and a re-fire would go out before it can.
        const held = p.Message === 'SOURCE_RESET_CONFIRM_REQUIRED' || p.Message === 'SEND_ALREADY_SENT';
        if (held) pending.current = false;
        if ((pending.current || superseded) && canWrite && !pausedRef.current && !held && mountedRef.current) {
            pending.current = false;
            // Terminates: editSeq only advances on human input, and `inFlight` guards re-entry.
            persist();
        }
        return { ok: good, message: good ? null : (p.Message ?? null) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId, channel, canWrite, dispatch, wa, screenSourceId, screenVersionId, pageChannel, pageId]);

    // Debounced trigger. Driven off the SLICE fields, not off the onChange handlers, so the
    // request body is built from fresh post-dispatch state (same reason as the e-mail side).
    //
    // 🔴 ONE EFFECT AND ONE TIMER, CARRYING BOTH KINDS OF UNSAVED WORK (v1.23 adds the channel
    // bit). Writing the channel switch as a SECOND debounce effect is the obvious shape and it
    // is wrong: both would assign `timer.current`, so whichever re-ran last would overwrite the
    // other's handle — and the loser's cleanup would then clear the winner's timer and bail on
    // its own condition. That is an ARMED AUTOSAVE DROPPED ON THE FLOOR, which is the exact
    // failure the e-mail side documents at length (`SmartSendScreen.tsx:201-217`) and the reason
    // `editSeq` exists. One timer cannot race itself.
    //
    // A template with zero placeholders has nothing to debounce — nothing on the tab can be
    // edited. 09-channel-units: its unit already exists whole (container, row and variable count,
    // written by POST WhatsApp/Container), and `openSummary` saves the unit before every summary.
    // The same bail covers the channel bit: without a template id `_SetMapping` answers -13, so
    // there is nothing a save could accomplish.
    const somethingUnsaved = !!wa?.waDirty || !!wa?.suppressEmailDirty;
    useEffect(() => {
        if (!canWrite) return undefined;
        // 12-fix-wave 7.4(d) — no new timer while the page holds autosave (the reset dialog, or a closed send).
        if (paused) return undefined;
        if (!somethingUnsaved) return undefined;
        if (!wa?.whatsAppTemplateId) return undefined;
        timer.current = setTimeout(() => { timer.current = null; persist(); }, DEBOUNCE_MS);
        return () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wa?.waVariableMap, wa?.suppressEmail, somethingUnsaved, wa?.whatsAppTemplateId, canWrite, paused]);

    return { markWaEdited, saveWaMapping: persist, cancelPending, canWrite };
};

export default useWaAutosave;
